import React, { useEffect, useState, useRef } from "react"
import { Box, Button, CardMedia, Container, Grid, Typography, useTheme } from "@mui/material"
import Carousel from "react-multi-carousel"
import { useAuthContext } from "../services/auth"
import { PagedVideoSuggestions } from "../components/VideoSuggestions"
import { ReactComponent as HyrisLogo } from "../assets/svg/hyris-full.svg"
import { ReactComponent as HyrisLogoChristmas } from "../assets/svg/hyris-full-christmas.svg"
import { ReactComponent as HyrisLogoHalloween } from "../assets/svg/hyris-full-halloween.svg"
import { useNavigate } from "react-router-dom"
import { VideoThumbnail } from "../components/VideoThumbnail"
import { useStaticContext } from "../services/static"
import { VideoQueryType } from "../services/api/video"
import { DirectCardMedia, DirectPoster } from "../components/DirectPoster"
import { CONSTANTS, FormatDate, FormatParagraphs, StylelessLink } from "../components/Util"
import { PageTitle } from "../components/StyledElements"

import { ReactComponent as DownIcon } from "../assets/svg/down.svg"
import { ReactComponent as PlayIcon } from "../assets/svg/play.svg"
import HyrisLogoV from "../assets/png/Logo_Hyris_BV.png"
import HyrisLogoP from "../assets/png/Logo_Hyris_BP.png"
import { Loading } from "../components/Loading"
import { Motd } from "../components/Motd"
import { DirectCarousel, VideoCarousel } from "../components/Carousels"
import useWindowDimensions from "../services/useWindowDimensions"

const now = new Date()
const currentYear = now.getFullYear()
const currentMonth = now.getMonth() + 1
const currentDay = now.getDay()
const currentDate = now.getDate()

const mainSplashTextPool: string[] = (
  currentMonth === 12 && currentDate === 25
    ? ["Joyeux Noël !", "Ho ho ho !", "🎄🎁 !", "Joyeuses fêtes !"]
    : currentMonth === 1 && currentDate === 1
      ? ["Bonne année !", "🎉🥂 !", "Joyeuses fêtes !", `${currentYear} !`]
      : currentMonth === 10 && currentDate === 31
        ? ["Joyeux Halloween !", "Bouh !", "Derrière toi !", "🎃👻 !"]
        : ["Bienvenue sur hyris.tv !"]
).map((s) => (currentMonth === 4 && currentDate === 1 ? s!.replace("!", "?") : s))

const altSplashTextPool: string[] = (
  [
    "Bienvenue sur hyris.tv !",
    "25 images par seconde !",
    "Bonsoir CentraleSupélec !",
    "Allez voir galerie.pics !",
    "Allez voir viarezo.fr !",
    "Allez voir sbcs-events.fr !",
    "Nouveau site !",
    "Poooolop !",
    "Vidéo et régie !",
    "NX x SMS !",
    "Depuis 1990 !",
    "100% d'étudiants !",
    "Vidéos faites maison !",
    `P${(currentYear + (currentMonth < 4 ? 1 : 2)) % 100} !`,
    "Hello, world !",
    "Hyris, c'est bien !",
    "Existe en mode jour !",
    "1080p !",
    "12 737 lignes de code !",
    "Plenty more to come !",
    "Testé en prod !",
    "PAS testé en prod !",
    "🗣🗣🔥🔥💙💙 !",
    `v${CONSTANTS.version} !`,
    "{WelcomeMessage} !",
    "Site codé avec amour !",
    "Avec des playlists !",
    "Hyris and chill !",
    "Action !",
    "Coupez !",
    currentMonth === 9 || currentMonth === 10 ? `Bienvenue aux P${(currentYear + 3) % 100} !` : null,
    currentDay === 1 ? "Comme un lundi !" : null,
    currentDay === 6 || currentDay === 0 ? "Bon weekend !" : null,
  ].filter((s) => s != null) as string[]
).map((s) => (currentMonth === 4 && currentDate === 1 ? s!.replace("!", "?") : s))

const altLogos = [HyrisLogoV, HyrisLogoP]

export const Home = (): React.ReactElement => {
  const { isAuth, loading } = useAuthContext()
  return loading ? <Loading /> : isAuth() ? <PrivateHome /> : <PublicHome />
}

const PrivateHome = (): React.ReactElement => {
  const theme = useTheme()
  const { width } = useWindowDimensions()
  const navigate = useNavigate()
  const {
    trendingVideos,
    trendingError,
    latestVideos,
    latestVideosError,
    latestDirects,
    latestDirectsError,
    homeDisplay,
    currentDirect,
    currentDirectPosterSource: posterSource,
    motd,
    loading: staticLoading,
  } = useStaticContext()

  useEffect(() => {
    if (trendingError) {
      console.error("Couldn't fetch trending videos", "error")
    }
    if (latestVideosError) {
      console.error("Couldn't fetch last videos", "error")
    }
    if (latestDirectsError) {
      console.error("Couldn't fetch last directs", "error")
    }
  }, [trendingError, latestVideosError, latestDirectsError])

  return (
    <Box
      sx={{
        pt: 0,
        pb: 8,
        px: 0,
        position: theme.name === "glass" ? "static" : "relative",
        overflow: theme.name === "glass" ? "visible" : "hidden",
      }}
    >
      {homeDisplay && currentDirect && posterSource && !staticLoading ? (
        <Box
          sx={{
            position: "absolute",
            top: "0",
            overflow: theme.name === "glass" ? "visible" : "hidden",
            width: "100%",
            minHeight: "100vh",
            maxHeight: "200vh",
          }}
        >
          <CardMedia
            component="img"
            image={posterSource}
            sx={{
              userSelect: "none",
              left: "50%",
              width: "100%",
              objectFit: "cover",
              zIndex: 100,
              transform: `translateY(min(${theme.name === "glass" ? -81 : 0}px,calc(300px - 30vw)))`,
              opacity: 0.6,
              filter: "blur(.5vw)",
              maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 20%, rgba(0,0,0,0) min(100%, 200vh))",
            }}
          />
        </Box>
      ) : (
        <> </>
      )}
      {!staticLoading ? (
        <Box sx={{ position: "relative", zIndex: 200 }}>
          {motd !== null && motd !== "" && <Motd motd={motd} />}
          <PageTitle>{"Bienvenue sur hyris.tv !"}</PageTitle>
          {(homeDisplay === 2 || homeDisplay === 3) && currentDirect ? (
            <Box borderBottom="1px solid darkgray" sx={{ py: 5, px: 3 }}>
              <Grid container spacing={3} direction="row" justifyContent={"center"}>
                {posterSource ? (
                  <Grid size={{ xs: 6, lg: 4 }} sx={{ display: { xs: "none", md: "block" } }}>
                    <DirectCardMedia image={posterSource} title={currentDirect.title} />
                  </Grid>
                ) : null}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography component="h3" variant="h3" fontSize={"2rem"}>
                    {homeDisplay === 2 ? "Prochain DT" : "DT en cours"}
                  </Typography>
                  <Typography component="h3" variant="h3" fontSize={"1.5rem"} fontWeight={600}>
                    {currentDirect.title}
                  </Typography>
                  {currentDirect.date && (
                    <Typography variant="subtitle1" fontSize={".8rem"}>
                      {FormatDate(currentDirect.date)}
                    </Typography>
                  )}
                  {currentDirect.liveUrl && homeDisplay === 3 && (
                    <Box
                      onClick={() => navigate("/dt")}
                      width="fit-content"
                      bgcolor="red"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignContent: "center",
                        justifyContent: "flex-start",
                      }}
                      sx={{
                        padding: 1,
                        paddingRight: 2,
                        flexShrink: 0,
                        textAlign: "left",
                        transition: ".3s",
                        cursor: "pointer",
                        borderRadius: "5px",
                        my: 4,
                        "&:hover": {
                          backgroundColor: theme.palette.mode === "light" ? "#ff3333" : "#c40000",
                        },
                      }}
                    >
                      <PlayIcon height="30px" width="30px" style={{ fill: "white", marginRight: "5px" }} />
                      <Typography fontWeight="bold" lineHeight={1.8} color="white">
                        {"LIVE"}
                      </Typography>
                    </Box>
                  )}
                  <Typography mt={4}>
                    <FormatParagraphs content={currentDirect.description} />
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <></>
          )}
          {trendingVideos.length > 0 && (
            <Box borderBottom="1px solid darkgray" sx={{ py: 5, px: 3 }}>
              <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
                {"A la une"}
              </Typography>
              <Grid container spacing={3} direction="row">
                {trendingVideos[0] && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <VideoThumbnail video={trendingVideos[0]} hideTrendingIcon />
                  </Grid>
                )}
                <Grid container spacing={3} size={{ xs: 12, md: 6 }} direction="row">
                  {trendingVideos[1] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoThumbnail video={trendingVideos[1]} hideTrendingIcon />
                    </Grid>
                  )}
                  {trendingVideos[2] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoThumbnail video={trendingVideos[2]} hideTrendingIcon />
                    </Grid>
                  )}
                  {trendingVideos[3] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoThumbnail video={trendingVideos[3]} hideTrendingIcon />
                    </Grid>
                  )}
                  {trendingVideos[4] && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <VideoThumbnail video={trendingVideos[4]} hideTrendingIcon />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
          <Box borderBottom="1px solid darkgray" sx={{ py: 5, px: 3 }}>
            <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
              <StylelessLink to="/videos">{"Nos dernières vidéos"}</StylelessLink>
            </Typography>
            {width >= theme.breakpoints.values.lg ? (
              <VideoCarousel videos={latestVideos.slice(0, 16)} />
            ) : (
              <Grid container spacing={1} direction="row" sx={{ py: 2 }}>
                {latestVideos.slice(0, 12).map((video) => (
                  <Grid key={video.id} size={{ xs: 12, sm: 4, md: 3 }}>
                    <VideoThumbnail video={video} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
          <Box sx={{ py: 5, px: 3 }}>
            <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
              <StylelessLink to="/directs">{"Nos derniers DT"}</StylelessLink>
            </Typography>
            {width >= theme.breakpoints.values.lg ? (
              <DirectCarousel directs={latestDirects.slice(0, 16)} />
            ) : (
              <Grid container spacing={1} direction="row" sx={{ py: 2 }}>
                {latestDirects.slice(0, 12).map((direct) => (
                  <Grid key={direct.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <DirectPoster direct={direct} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  )
}

const funValueThreshold = 0.01

const PublicHome = (): React.ReactElement => {
  const { isAuth, logout } = useAuthContext()
  const navigate = useNavigate()
  const theme = useTheme()

  const [splashText] = useState<string>(
    Math.random() > 0.25
      ? mainSplashTextPool[Math.floor(Math.random() * mainSplashTextPool.length)]
      : altSplashTextPool[Math.floor(Math.random() * altSplashTextPool.length)],
  )
  const [funValue] = useState(Math.random())

  const handleLoginClick = () => {
    if (isAuth()) {
      logout()
    }
    navigate("/login")
  }

  return (
    <>
      <Box
        sx={{
          pt: 8,
          pb: 6,
          px: 0,
          width: "100%",
          height: "90vh",
          position: "relative",
          overflow: theme.name === "glass" ? "visible" : "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: theme.name === "glass" ? "100vh" : "100%",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <VideoBackground />
        </Box>
        <Container maxWidth="md" sx={{ mb: 8, position: "relative", zIndex: 200 }}>
          <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center">
            <Grid size={12} display="flex" justifyContent="center">
              {funValue > funValueThreshold ? (
                theme.name === "christmas" ? (
                  <HyrisLogoChristmas
                    fill="white"
                    style={{ maxWidth: "60%", maxHeight: "40vh", width: "800px", height: "auto" }}
                  />
                ) : theme.name === "halloween" ? (
                  <HyrisLogoHalloween
                    fill="white"
                    style={{ maxWidth: "60%", maxHeight: "40vh", width: "800px", height: "auto" }}
                  />
                ) : (
                  <HyrisLogo
                    fill="white"
                    style={{ maxWidth: "60%", maxHeight: "40vh", width: "800px", height: "auto" }}
                  />
                )
              ) : (
                <img
                  src={altLogos[Math.floor((funValue / funValueThreshold) * altLogos.length)]}
                  style={{ maxWidth: "60%", maxHeight: "40vh", userSelect: "none" }}
                />
              )}
            </Grid>
            <Box
              sx={{
                my: 1,
                width: "100%",
              }}
            >
              <Typography
                component="h1"
                variant="h2"
                align="center"
                color="common.white"
                gutterBottom
                sx={{ textShadow: "1px 1px 2px black", userSelect: "none" }}
              >
                {splashText}
              </Typography>
            </Box>
            <Grid container gap={5} justifyContent="center">
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleLoginClick}
                sx={{
                  fontWeight: "bold",
                  borderRadius: theme.name === "classic" ? "100px" : undefined,
                  padding: theme.name === "classic" ? "20px" : undefined,
                }}
              >
                {"Connexion"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => navigate("/contact")}
                sx={{
                  backgroundColor: "black",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "black",
                  },
                  borderWidth: theme.name === "classic" ? 0 : undefined,
                  borderRadius: theme.name === "classic" ? "100px" : undefined,
                  padding: theme.name === "classic" ? "20px" : undefined,
                }}
              >
                {"Contact"}
              </Button>
            </Grid>
            <ScrollerButton />
          </Grid>
        </Container>
      </Box>
      <Box px={4} py={8}>
        <Typography component="h3" variant="h3" fontSize={"2rem"} mb={2}>
          {"Nos vidéos publiques"}
        </Typography>
        <PagedVideoSuggestions constraint={VideoQueryType.LATEST} tagFilter={[]} md={3} pageSize={48} hideTopControls />
      </Box>
    </>
  )
}

const ScrollerButton = (): React.ReactElement => {
  const [scrollerOpacity, setScrollerOpacity] = useState<number>(1)

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY
      setScrollerOpacity((200 - offset) / 100)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])
  return (
    <Box mt={8} display="flex" justifyContent="center" width="100px" maxWidth="100%">
      <DownIcon
        fill="white"
        height={"50px"}
        opacity={scrollerOpacity}
        style={{
          visibility: scrollerOpacity <= 0 ? "hidden" : "visible",
          cursor: scrollerOpacity > 0 ? "pointer" : "default",
        }}
        onClick={() => {
          window.scrollTo({ top: window.innerHeight - 80, behavior: "smooth" })
        }}
      />
    </Box>
  )
}

const VideoBackground = (): React.ReactElement => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const theme = useTheme()
  const [ready, setReady] = useState<boolean>(false)

  const handleCanPlay = () => setReady(true)
  const handleError = () => setReady(false)

  const bannerVideoSource = theme.name ? `/api/media/banner/${theme.name}` : "/api/media/banner"

  return (
    <video
      ref={videoRef}
      autoPlay
      loop
      muted
      playsInline
      controls={false}
      onCanPlay={handleCanPlay}
      onCanPlayThrough={handleCanPlay}
      onError={handleError}
      style={{
        position: "absolute",
        minWidth: "100%",
        minHeight: "100%",
        width: "auto",
        height: "auto",
        zIndex: 100,
        opacity: ready ? 1 : 0,
        pointerEvents: "none",
      }}
    >
      <source src={bannerVideoSource} type="video/mp4" />
    </video>
  )
}
